import {Checkbox} from 'antd';
import {CheckboxValueType} from 'antd/es/checkbox/Group';
import React, {FC, useEffect, useState} from 'react';

import {IFieldMeta} from 'entities/metadata';
import {setData} from 'modules/data/data-actions';
import {
    selectEntityData,
    selectFormEntityData,
    selectUsedAdditionalOptions,
} from 'modules/data/data-selectors';
import {IFormEntityData} from 'modules/data/data-types';
import {EEntityType} from 'shared/constants/entities';
import {useAppDispatch, useAppSelector} from 'store/config/hooks';

import s from './checkbox-group.module.less';

export interface ICheckboxOption {
    active: string;
    docTemplateId: number;
    metaTableName: number;
    metaFieldKey: string;
    fieldLabel: string;
    isRequired: string;
}

interface ICheckboxGroupProps {
    referenceUrl: string;
    onChange: (value: CheckboxValueType[]) => void;
    entityName: string;
    fieldMeta: IFieldMeta;
}

export const CheckboxGroup: FC<ICheckboxGroupProps> = ({
    referenceUrl,
    entityName,
    onChange,
    fieldMeta,
}: ICheckboxGroupProps) => {
    const dispatch = useAppDispatch();
    const data: ICheckboxOption[] | undefined = useAppSelector(selectUsedAdditionalOptions(entityName, referenceUrl));
    const formData = useAppSelector(selectEntityData(entityName, EEntityType.FORM)) as IFormEntityData;
    const mappingDtoList: ICheckboxOption[] | undefined = useAppSelector(
        selectFormEntityData(entityName),
    )?.data?.settingDocumentTemplateMappingDtoList;
    const [options, setOptions] = useState<ICheckboxOption[]>([]);
    const [checkedList, setCheckedList] = useState<CheckboxValueType[]>([]);
    const [indeterminate, setIndeterminate] = useState(false);
    const [checkAll, setCheckAll] = useState(true);
    const [isInitialized, setIsInitialized] = useState(false);

    const renderOptions = () => options.map(option => (
        <Checkbox
            key={option.metaFieldKey}
            value={option.metaFieldKey}
            disabled={option.isRequired === 'Y'}
        >
            {option.fieldLabel}
        </Checkbox>
    ));

    const normalizeValue = (currentValue: ICheckboxOption[]) => {
        let resultValue: string[] = [];
        if (Array.isArray(currentValue)) {
            resultValue = currentValue?.map(record => (record.metaFieldKey));
        }
        return resultValue;
    };

    const setFilteredCheckedList = (
        currentValue: ICheckboxOption[],
    ) => {
        const resultValue = currentValue.filter(record => record.active === 'Y')
            .map(record => (
                record.metaFieldKey
            ));
        setCheckedList(resultValue);
        onChange(resultValue);
        return resultValue;
    };

    const initializeDefaultValue = (
        currentValue: ICheckboxOption[],
    ) => {
        const resultValue = setFilteredCheckedList(currentValue);
        if (!formData?.data[fieldMeta.key] && resultValue) {
            formData.data[fieldMeta.key] = resultValue;
            dispatch(setData({
                entityName,
                entityType: EEntityType.FORM,
                data: formData.data,
            }));
        }
    };

    const handleChange = (list: CheckboxValueType[]) => {
        setCheckedList(list);
        setIndeterminate(!!list.length && list.length < options.length);
        setCheckAll(list.length === options.length);
        onChange(list);
    };

    const onCheckAllChange = () => {
        setCheckAll(prevCheckAll => !prevCheckAll);
        const boolValue = (checkedList.length !== mappingDtoList?.length) && !checkAll ? 'Y' : 'N';

        const changedOptions = options.map(option => ({
            ...option,
            active: option.isRequired === 'Y' ? option.active : boolValue,
        }));
        const filteredOptionsChecked = changedOptions.filter(option => option.active === 'Y');
        setCheckedList(normalizeValue(filteredOptionsChecked));
        setIndeterminate(filteredOptionsChecked.length < options.length);
        onChange(normalizeValue(filteredOptionsChecked));
    };

    useEffect(() => {
        if (mappingDtoList?.length) {
            setOptions(mappingDtoList);
            initializeDefaultValue(mappingDtoList);
            setIsInitialized(true);
        }
    }, [mappingDtoList]);

    useEffect(() => {
        if (data?.length || data?.length === 0) {
            setOptions(data);
            // инициализация для формы создания
            if (!isInitialized) {
                setIsInitialized(true);
            }
            setFilteredCheckedList(data);
        }
    }, [data]);

    if (options.length === 0) return null;

    return (
        <div className={s['scroll-container']}>
            <div className={s['checkbox-group']}>
                <div className={s['checked-all-indicator']}>
                    <Checkbox
                        indeterminate={indeterminate}
                        onChange={onCheckAllChange}
                        checked={checkAll}
                    >
                        Выбрать все
                    </Checkbox>
                </div>
                <Checkbox.Group
                    onChange={handleChange}
                    value={checkedList}
                >
                    {renderOptions()}
                </Checkbox.Group>
            </div>
        </div>
    );
};
